import type { DGEUpdateLicensePayload, License } from '@img-arena/consoles-api-streaming'

export const toCustomISOString = (date: Date) => {
  return date.toISOString().replace(/\.\d{3}Z$/, '+0000')
}

type FormikValues = {
  licenseSportPropertyId: number | undefined
  allowLowLatencyPlayer: boolean
  archiveToDve: boolean
  archiveToS3: boolean
  allowLiveDvr: boolean
  allowApiVodAccess: boolean
  allowInteractiveStreaming: boolean
  allowSrtSyndication: boolean
  bitRate: number
  autoBooking: boolean
  geoBlockingType: 'none' | 'whitelist' | 'blacklist'
  geoBlockingTerritories: string[]
  licensingPeriods: Array<{
    maxEvents?: number
    purchasedEvents?: number
    startDate: string
    endDate: string
    year: string
    id?: number
    version?: number
  }>
}

export const generateUpdateLicensePayload = (
  currentLicense: License,
  values: FormikValues
): DGEUpdateLicensePayload => {
  const payload = {
    actualPeriod: currentLicense.actualPeriod,
    allowLLP: values.allowLowLatencyPlayer,
    allowLiveDVR: values.allowLiveDvr,
    allowApiVodAccess: values.allowApiVodAccess,
    allowSrtSyndication: values.allowSrtSyndication,
    autobookingEnabled: values.autoBooking,
    createdAt: currentLicense.createdAt,
    interactiveStreaming: values.allowInteractiveStreaming,
    geoListField:
      values.geoBlockingType === 'none' ? '[]' : JSON.stringify(values.geoBlockingTerritories),
    isWhiteList: values.geoBlockingType === 'whitelist',
    maxBitrateAllowed: values.bitRate,
    isDeleted: currentLicense.isDeleted,
    maxEvents: values.autoBooking
      ? -1
      : values.licensingPeriods.reduce((sum: number, period) => sum + (period.maxEvents || 0), 0),
    id: currentLicense.id,
    periods: values.licensingPeriods.map((period: FormikValues['licensingPeriods'][0]) => ({
      start: toCustomISOString(new Date(period.startDate)),
      end: toCustomISOString(new Date(period.endDate)),
      maxEvents: values.autoBooking ? null : (period.maxEvents ?? null),
      purchasedEvents: values.autoBooking ? null : (period.purchasedEvents ?? null),
      version: period.version,
      id: period.id
    })),
    storeVod: values.archiveToDve,
    storePermanentVod: values.archiveToS3
  }

  return payload
}

export const generateCreateLicensePayload = (operatorId: number, values: FormikValues) => {
  const payload = {
    allowLLP: values.allowLowLatencyPlayer,
    allowLiveDVR: values.allowLiveDvr,
    allowApiVodAccess: values.allowApiVodAccess,
    allowSrtSyndication: values.allowSrtSyndication,
    autobookingEnabled: values.autoBooking,
    interactiveStreaming: values.allowInteractiveStreaming,
    geoListField:
      values.geoBlockingType === 'none' ? '[]' : JSON.stringify(values.geoBlockingTerritories),
    isWhiteList: values.geoBlockingType === 'whitelist',
    maxBitrateAllowed: values.bitRate,
    maxEvents: values.autoBooking
      ? -1
      : values.licensingPeriods.reduce((sum: number, period) => sum + (period.maxEvents || 0), 0),
    operator: { id: operatorId },
    periods: values.licensingPeriods.map((period: FormikValues['licensingPeriods'][0]) => ({
      start: toCustomISOString(new Date(period.startDate)),
      end: toCustomISOString(new Date(period.endDate)),
      maxEvents: values.autoBooking ? null : (period.maxEvents ?? null),
      purchasedEvents: values.autoBooking ? null : (period.purchasedEvents ?? null)
    })),
    property: { id: values.licenseSportPropertyId! },
    storeVod: values.archiveToDve,
    storePermanentVod: values.archiveToS3
  }

  return payload
}
